/**
 * VoiceSettings — Popover for TTS voice selection and speech rate.
 */

import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  Slider,
  TextField,
  Tooltip,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

interface VoiceSettingsProps {
  ttsVoice: string;
  onVoiceChange: (voice: string) => void;
  voiceRate: number;
  onRateChange: (rate: number) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export default function VoiceSettings({
  ttsVoice,
  onVoiceChange,
  voiceRate,
  onRateChange,
  disabled,
  sx,
}: VoiceSettingsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Voice settings" arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={disabled}
          sx={sx}
          aria-label="Voice settings"
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Voice Output
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Model voice"
            value={ttsVoice}
            onChange={(e) => onVoiceChange(e.target.value)}
            disabled={disabled}
            helperText="Leave blank for the provider default voice"
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" gutterBottom>
            Speed: {voiceRate.toFixed(1)}x
          </Typography>
          <Slider
            min={0.5}
            max={2}
            step={0.1}
            value={voiceRate}
            onChange={(_, val) => onRateChange(val as number)}
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>
      </Popover>
    </>
  );
}
